import React from 'react';
import { WebsiteDesignProps } from './types';
import { Activity, Heart, Stethoscope, Plus } from 'lucide-react';
import { Button } from "@/components/ui/button";

const MedicalTrust: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Health Sciences Academy";

    return (
        <div className="bg-white min-h-screen font-sans text-slate-700">
            <nav className="container mx-auto px-6 py-6 flex justify-between items-center border-b border-slate-100">
                <div className="flex items-center gap-2 text-[#0ea5e9] font-bold text-2xl tracking-tight">
                    <Plus strokeWidth={4} /> {schoolName}
                </div>
                <div className="hidden md:flex gap-8 font-medium text-slate-500">
                    <a href="#" className="hover:text-[#0ea5e9] transition-colors">Programs</a>
                    <a href="#" className="hover:text-[#0ea5e9] transition-colors">Research</a>
                    <a href="#" className="hover:text-[#0ea5e9] transition-colors">Patients</a>
                    <a href="#" className="hover:text-[#0ea5e9] transition-colors">Staff</a>
                </div>
                <Button className="bg-[#0ea5e9] hover:bg-[#0284c7] text-white rounded-full px-8 font-medium shadow-lg shadow-sky-200">
                    Portal Login
                </Button>
            </nav>

            <main>
                <section className="py-20 bg-slate-50">
                    <div className="container mx-auto px-6 flex flex-col md:flex-row items-center gap-16">
                        <div className="md:w-1/2">
                            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                                Advancing the Future of <br/> <span className="text-[#0ea5e9]">Healthcare Education.</span>
                            </h1>
                            <p className="text-lg text-slate-500 mb-10 leading-relaxed">
                                We are dedicated to training compassionate, skilled, and innovative healthcare professionals through cutting-edge curriculum and clinical practice.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Button className="h-12 px-8 bg-white text-[#0ea5e9] border border-[#0ea5e9] hover:bg-[#0ea5e9] hover:text-white font-medium rounded-lg transition-colors">
                                    Explore Degrees
                                </Button>
                                <Button variant="link" className="h-12 text-slate-600 hover:text-[#0ea5e9]">
                                    Continuing Education &rarr;
                                </Button>
                            </div>
                        </div>
                        <div className="md:w-1/2 relative">
                            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                                <img src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=2070&auto=format&fit=crop" className="w-full object-cover" alt="Medical" />
                            </div>
                            {/* Decorative blobs */}
                            <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#0ea5e9]/10 rounded-full z-0"></div>
                            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#0ea5e9]/10 rounded-full z-0"></div>
                        </div>
                    </div>
                </section>

                <section className="py-20 container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Focus Areas</h2>
                        <p className="text-slate-500 max-w-2xl mx-auto">Providing comprehensive training across multiple disciplines within the medical field.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { icon: Stethoscope, title: "Clinical Practice", color: "text-blue-500", bg: "bg-blue-50" },
                            { icon: Activity, title: "Medical Tech", color: "text-emerald-500", bg: "bg-emerald-50" },
                            { icon: Heart, title: "Patient Care", color: "text-rose-500", bg: "bg-rose-50" }
                        ].map((item, i) => (
                            <div key={i} className="p-8 rounded-xl border border-slate-100 hover:shadow-lg transition-shadow cursor-pointer">
                                <div className={`w-14 h-14 ${item.bg} ${item.color} rounded-full flex items-center justify-center mb-6`}>
                                    <item.icon size={28} />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                                <p className="text-slate-500 leading-relaxed">Rigorous training protocols designed to meet modern healthcare standards.</p>
                            </div>
                        ))}
                    </div>
                </section>
            </main>
        </div>
    );
};

export default MedicalTrust;
